/**
 * Tax classes — `/catalog/tax-classes`.
 *
 * A tax class says what KIND of thing is being sold. It carries no rate: rates
 * live in the finance tax codes, deliberately outside this contract, because a
 * rate changes when a government changes it and a classification does not.
 *
 * The general-ledger mapping is the join between those two worlds — which
 * account this class's sales tax and purchase tax land in. It is shown beside
 * the class rather than on a separate screen, because "which account does this
 * post to" is the first question anyone asks about a class.
 *
 * Two contract facts the screen respects rather than hides. The list is a
 * lookup — id and name only — so reading a class fetches the rest instead of
 * pretending the list already had it. And every write carries If-Match, so a
 * stale save is refused as a conflict instead of quietly overwriting somebody
 * else's edit.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { newIdempotencyKey } from "@/features/shared/ops-api";
import { isPreconditionFailed } from "@/features/shared/ops-api";
import { useAccounts } from "@/features/ledger/queries";

import {
  createTaxClass,
  deleteTaxClass,
  deleteTaxClassGlMapping,
  getTaxClass,
  getTaxClassGlMapping,
  listTaxClasses,
  putTaxClassGlMapping,
  updateTaxClass,
  type TaxClass,
} from "../tax-classes-api";

const LIST_KEY = ["catalog/tax-classes", "manage"];

export interface TaxClassesPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canReadAccounts?: boolean;
}

export function TaxClassesPage({
  canCreate = false,
  canUpdate = false,
  canReadAccounts = false,
}: TaxClassesPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const classes = useQuery({
    queryKey: LIST_KEY,
    queryFn: async ({ signal }) => [...(await listTaxClasses({}, signal)).items],
  });

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("catalog.taxClasses.title", "Tax classes")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "catalog.taxClasses.subtitle",
              "What kind of thing is being sold. The rate is a tax code's business; this is the classification and the accounts it posts to.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button type="button" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("catalog.taxClasses.new", "New tax class")}
          </Button>
        ) : null}
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <Card>
          <CardContent className="p-2">
            {classes.isLoading ? (
              <p className="p-2 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : (classes.data ?? []).length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">
                {t("catalog.taxClasses.empty", "No tax class is defined.")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {(classes.data ?? []).map((lookup) => (
                  <li key={lookup.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(lookup.id)}
                      aria-current={lookup.id === selectedId ? "true" : undefined}
                      className={`w-full rounded-md border p-2 text-start text-sm hover:bg-muted/50 ${
                        lookup.id === selectedId ? "border-primary bg-muted/40" : ""
                      }`}
                    >
                      {lookup.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {selectedId === null ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">
                {t("catalog.taxClasses.pick", "Pick a class to see its detail and its accounts.")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <TaxClassDetail
            taxClassId={selectedId}
            canUpdate={canUpdate}
            canReadAccounts={canReadAccounts}
            onDeleted={() => {
              setSelectedId(null);
              void queryClient.invalidateQueries({ queryKey: LIST_KEY });
            }}
            onError={setError}
          />
        )}
      </div>

      {creating ? (
        <NewTaxClassDialog
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setSelectedId(created.id);
            void queryClient.invalidateQueries({ queryKey: LIST_KEY });
          }}
        />
      ) : null}
    </div>
  );
}

function TaxClassDetail({
  taxClassId,
  canUpdate,
  canReadAccounts,
  onDeleted,
  onError,
}: {
  taxClassId: string;
  canUpdate: boolean;
  canReadAccounts: boolean;
  onDeleted: () => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<{
    name: string;
    name_ar: string;
    description: string;
    status: string;
  } | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const detail = useQuery({
    queryKey: ["catalog/tax-class", taxClassId],
    queryFn: () => getTaxClass(taxClassId),
  });
  const mapping = useQuery({
    queryKey: ["catalog/tax-class-mapping", taxClassId],
    // A class with no mapping is a 404, which is an answer rather than a
    // failure: it means nothing has been mapped yet.
    queryFn: () => getTaxClassGlMapping(taxClassId).catch(() => null),
    retry: false,
  });
  const accounts = useAccounts({ is_postable: true, active: true }, canReadAccounts);

  useEffect(() => {
    if (detail.data !== undefined) {
      setForm({
        name: detail.data.data.name,
        name_ar: detail.data.data.name_ar ?? "",
        description: detail.data.data.description,
        status: detail.data.data.status,
      });
      setEtag(detail.data.etag);
    }
  }, [detail.data]);

  const accountOptions = (accounts.data ?? []).map((account) => ({
    value: account.id,
    label: `${account.code} · ${account.name}`,
  }));

  const save = () => {
    if (form === null || etag === null) return;
    setBusy(true);
    onError(null);
    void updateTaxClass(
      taxClassId,
      {
        name: form.name.trim(),
        description: form.description,
        status: form.status as TaxClass["status"],
        // An explicit null clears the Arabic name; an empty string is not a
        // name and must not be sent as one.
        name_ar: form.name_ar.trim() === "" ? null : form.name_ar.trim(),
      },
      etag,
    )
      .then(async (updated) => {
        setEtag(updated.etag);
        await queryClient.invalidateQueries({ queryKey: ["catalog/tax-classes", "manage"] });
      })
      .catch((error: unknown) =>
        onError(
          isPreconditionFailed(error)
            ? t(
                "catalog.taxClasses.conflict",
                "Somebody else changed this class. Reload it before saving, so their edit is not overwritten.",
              )
            : t("catalog.taxClasses.saveError", "The class could not be saved."),
        ),
      )
      .finally(() => setBusy(false));
  };

  const saveMapping = (sales: string | null, purchase: string | null) => {
    if (etag === null) return;
    setBusy(true);
    onError(null);
    // The mapping and the class share one version, so the write is guarded by
    // the class's tag and bumps it — the new tag is carried straight back into
    // the form's precondition rather than left stale.
    void putTaxClassGlMapping(
      taxClassId,
      { sales_tax_account_id: sales, purchase_tax_account_id: purchase },
      etag,
    )
      .then(async (written) => {
        setEtag(written.etag);
        await queryClient.invalidateQueries({ queryKey: ["catalog/tax-class-mapping"] });
        await queryClient.invalidateQueries({ queryKey: ["catalog/tax-class", taxClassId] });
      })
      .catch(() =>
        onError(t("catalog.taxClasses.mappingError", "The accounts could not be saved.")),
      )
      .finally(() => setBusy(false));
  };

  if (detail.isLoading || form === null) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
        </CardContent>
      </Card>
    );
  }

  const current = mapping.data?.data ?? null;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{detail.data?.data.name}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">
              <span dir="ltr" className="font-mono">
                {detail.data?.data.code}
              </span>
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t(
                "catalog.taxClasses.codeFixed",
                "A code is what other records cite; it cannot move.",
              )}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tax-class-name">{t("catalog.taxClasses.name", "Name")}</Label>
              <Input
                id="tax-class-name"
                value={form.name}
                disabled={!canUpdate}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tax-class-name-ar">
                {t("catalog.taxClasses.nameAr", "Name (Arabic)")}
              </Label>
              <Input
                id="tax-class-name-ar"
                dir="rtl"
                value={form.name_ar}
                disabled={!canUpdate}
                onChange={(event) => setForm({ ...form, name_ar: event.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="tax-class-description">
                {t("catalog.taxClasses.description", "Description")}
              </Label>
              <Input
                id="tax-class-description"
                value={form.description}
                disabled={!canUpdate}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tax-class-status">{t("catalog.taxClasses.status", "Status")}</Label>
              <Select
                value={form.status}
                disabled={!canUpdate}
                onValueChange={(value) => setForm({ ...form, status: value })}
              >
                <SelectTrigger id="tax-class-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("catalog.status.active", "Active")}</SelectItem>
                  <SelectItem value="archived">
                    {t("catalog.status.archived", "Archived")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {canUpdate ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" disabled={busy} onClick={save}>
                {t("actions.save", "Save")}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy || etag === null}
                onClick={() => {
                  setBusy(true);
                  onError(null);
                  void deleteTaxClass(taxClassId, etag as string)
                    .then(onDeleted)
                    .catch(() =>
                      onError(
                        t(
                          "catalog.taxClasses.deleteError",
                          "The class could not be deleted. Something is probably still classified by it.",
                        ),
                      ),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                {t("actions.delete", "Delete")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("catalog.taxClasses.mappingTitle", "Where its tax posts")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "catalog.taxClasses.mappingHint",
              "The account this class's sales tax and purchase tax land in. Leaving one empty means this class does not decide it, and something else must.",
            )}
          </p>
          {!canReadAccounts ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "catalog.taxClasses.needsAccounts",
                "Reading the chart of accounts is what names these accounts; without it they would be identifiers.",
              )}
            </p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="tax-class-sales">
                    {t("catalog.taxClasses.salesAccount", "Sales tax account")}
                  </Label>
                  <Combobox
                    id="tax-class-sales"
                    options={accountOptions}
                    value={current?.sales_tax_account_id ?? null}
                    disabled={!canUpdate || busy}
                    onChange={(value) =>
                      saveMapping(value, current?.purchase_tax_account_id ?? null)
                    }
                    placeholder={t("catalog.taxClasses.noAccount", "Not mapped")}
                    searchPlaceholder={t("actions.search", "Search")}
                    emptyMessage={t("catalog.taxClasses.noAccounts", "No account matches.")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tax-class-purchase">
                    {t("catalog.taxClasses.purchaseAccount", "Purchase tax account")}
                  </Label>
                  <Combobox
                    id="tax-class-purchase"
                    options={accountOptions}
                    value={current?.purchase_tax_account_id ?? null}
                    disabled={!canUpdate || busy}
                    onChange={(value) => saveMapping(current?.sales_tax_account_id ?? null, value)}
                    placeholder={t("catalog.taxClasses.noAccount", "Not mapped")}
                    searchPlaceholder={t("actions.search", "Search")}
                    emptyMessage={t("catalog.taxClasses.noAccounts", "No account matches.")}
                  />
                </div>
              </div>
              {canUpdate && current !== null ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    onError(null);
                    void deleteTaxClassGlMapping(taxClassId)
                      .then(async () => {
                        await queryClient.invalidateQueries({
                          queryKey: ["catalog/tax-class-mapping"],
                        });
                        await queryClient.invalidateQueries({
                          queryKey: ["catalog/tax-class", taxClassId],
                        });
                      })
                      .catch(() =>
                        onError(
                          t("catalog.taxClasses.mappingError", "The accounts could not be saved."),
                        ),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  {t("catalog.taxClasses.removeMapping", "Remove both")}
                </Button>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NewTaxClassDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (created: TaxClass) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ code: "", name: "", name_ar: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = form.code.trim() !== "" && form.name.trim() !== "";

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("catalog.taxClasses.new", "New tax class")}</DialogTitle>
          <DialogDescription>
            {t(
              "catalog.taxClasses.newHint",
              "The code is what other records will cite, so it is fixed once the class exists.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-tax-class-code">{t("catalog.taxClasses.code", "Code")}</Label>
            <Input
              id="new-tax-class-code"
              dir="ltr"
              className="font-mono"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-tax-class-name">{t("catalog.taxClasses.name", "Name")}</Label>
            <Input
              id="new-tax-class-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-tax-class-name-ar">
              {t("catalog.taxClasses.nameAr", "Name (Arabic)")}
            </Label>
            <Input
              id="new-tax-class-name-ar"
              dir="rtl"
              value={form.name_ar}
              onChange={(event) => setForm({ ...form, name_ar: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-tax-class-description">
              {t("catalog.taxClasses.description", "Description")}
            </Label>
            <Input
              id="new-tax-class-description"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </div>
        </div>
        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("actions.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            disabled={!complete || busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              void createTaxClass(
                {
                  code: form.code.trim(),
                  name: form.name.trim(),
                  description: form.description,
                  ...(form.name_ar.trim() === "" ? {} : { name_ar: form.name_ar.trim() }),
                },
                newIdempotencyKey(),
              )
                .then((created) => {
                  onCreated(created);
                  onClose();
                })
                .catch(() =>
                  setError(t("catalog.taxClasses.createError", "The class could not be created.")),
                )
                .finally(() => setBusy(false));
            }}
          >
            {t("actions.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
